import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/hooks/useAuth';
import { sectionForPath } from './nav-config';
import { useOrderedSections, useNavPrefs, useSectionLanding } from '@/shared/store/navPrefsStore';
import { usePriorityOverflow, isShown } from '@/shared/hooks/usePriorityOverflow';
import {
  IconChevron, IconSettings, IconSearch,
  IconPalette, IconGrid, IconShield, IconLock, IconLogOut, IconHelp, IconGrip,
} from '@/components/ui/Icon';
import { useHelpStore } from '@/shared/store/helpStore';
import { DropdownMenu, type DropdownItem } from '@/components/ui/DropdownMenu';
import { useMediaQuery, NARROW_QUERY } from '@/shared/hooks/useMediaQuery';
import { useBranchMenu } from './useBranchMenu';
import { BranchPicker } from './BranchPicker';
import { CustomNavTabs, useCustomNavEntries } from './CustomNavTabs';
import { NotificationBell } from './NotificationBell';
import { isPlatformAccount } from '@/shared/platformAccount';
import { IconButton } from '@/components/ui/IconButton';
import { FavoritesMenu } from '@/features/favorites/FavoritesMenu';
import { Modal } from '@/components/ui/Modal';
import { AppearancePage } from '@/features/settings/AppearancePage';
import { SecurityPage } from '@/features/settings/SecurityPage';
import { ChangePasswordPage } from '@/features/settings/ChangePasswordPage';
import { MenuDesignPage } from '@/features/settings/MenuDesignPage';

type ModalKey = null | 'appearance' | 'menu-design' | 'security' | 'change-password';

const MODAL_TITLE: Record<Exclude<ModalKey, null>, string> = {
  'appearance': 'Appearance',
  'menu-design': 'Menu design',
  'security': 'Security',
  'change-password': 'Change password',
};

const MODAL_WIDTH: Record<Exclude<ModalKey, null>, string> = {
  'appearance': 'max-w-3xl',
  'menu-design': 'max-w-xl',
  'security': 'max-w-3xl',
  'change-password': 'max-w-md',
};

export function MainNav() {
  const { user, company, companies, logout, switchCompany } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const active = sectionForPath(location.pathname);
  const [modal, setModal] = useState<ModalKey>(null);
  const sections = useOrderedSections();
  const sectionLanding = useSectionLanding();
  const compact = useNavPrefs((s) => s.compact);
  const navRef = useRef<HTMLElement | null>(null);
  const openHelp = useHelpStore((s) => s.openHelp);
  // Below md the company, branch and user pills are hidden (no room): the
  // Settings menu says who and where you are, and carries the switchers.
  const narrow = useMediaQuery(NARROW_QUERY);
  const branchMenu = useBranchMenu();

  // In-place "Menu Design": flip the real nav tabs into a drag-to-reorder
  // surface (like the invoice line / form designer) instead of opening a modal.
  const designing = useNavPrefs((s) => s.designing);
  const setDesigning = useNavPrefs((s) => s.setDesigning);
  const setMainOrder = useNavPrefs((s) => s.setMainOrder);
  const resetPrefs = useNavPrefs((s) => s.reset);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);
  const moveTab = (from: string, to: string) => {
    if (!from || from === to) return;
    const keys = sections.map((s) => s.key);
    const fromIdx = keys.indexOf(from);
    const toIdx = keys.indexOf(to);
    if (fromIdx < 0 || toIdx < 0) return;
    keys.splice(fromIdx, 1);
    keys.splice(toIdx, 0, from);
    setMainOrder(keys);
  };

  // Priority+ overflow: keep the tab strip on ONE fixed-width row. When the
  // tabs can't all fit — narrow window, larger browser font, or page zoom —
  // collapse the trailing tabs one-by-one into a "More" dropdown instead of
  // letting them grow/spill the nav container. Widths come from a hidden
  // measurement row (all tabs at full size + a "More" sentinel) so the
  // calculation is independent of what's currently shown.
  // The custom tabs share the row after the sections: reserve their live
  // width (each plus the 4px gap), re-measured when their number changes.
  const measureRef = useRef<HTMLDivElement | null>(null);
  const customNav = useCustomNavEntries();
  const customCount = customNav.items.length + customNav.objects.length;
  // The active section is pinned: it never moves into More, so activating or
  // focusing a tab (it grows to show its label) cannot drop keyboard focus.
  const activeIndex = sections.findIndex((s) => s.key === active?.key);
  const visibleCount = usePriorityOverflow({
    containerRef: navRef, measureRef, count: sections.length, gap: 4, activeIndex,
    enabled: !designing, deps: [sections, compact, customCount, location.pathname],
    reserve: () => Array.from(navRef.current?.querySelectorAll<HTMLElement>('[data-custom-navtab]') ?? [])
      .reduce((w, el) => w + el.offsetWidth + 4, 0),
  });

  // Shared tab geometry so the hidden measurer, the visible tabs, and the
  // "More" pill all size identically.
  const tabClass = (isActive: boolean) =>
    `${compact ? 'w-[24px] h-[24px] justify-center' : 'px-2 h-[24px] gap-1.5'} rounded-full text-[0.75rem] font-medium tracking-tight inline-flex items-center transition-colors duration-150 border focus:outline-none focus-visible:ring-2 focus-visible:ring-inset ${
      isActive
        ? 'bg-primary text-white font-semibold border-primary shadow-sm focus-visible:ring-white'
        : 'text-slate-900 hover:text-primary-text border-transparent hover:bg-slate-200 focus-visible:ring-primary'
    }`;
  // The More pill is the content of DropdownMenu's trigger button, which draws
  // the keyboard outline around it; the pill itself is never focused.
  const moreClass = (isActive: boolean) =>
    `${compact ? 'px-1.5 h-[24px] gap-0.5' : 'px-2 h-[24px] gap-1'} rounded-full text-[0.75rem] font-medium inline-flex items-center border cursor-pointer select-none ${
      isActive
        ? 'bg-primary text-white border-primary shadow-sm'
        : 'text-slate-900 hover:text-primary-text border-transparent hover:bg-slate-200'
    }`;

  // Company switcher rows: a lettermark avatar + the FULL company name
  // (nowrap — the menu grows instead of cropping), code underneath.
  const companyItems = (companies || [])
    .filter((c) => c.id !== company?.id)
    .map((c) => ({
      label: (
        <span className="flex flex-col leading-tight whitespace-nowrap">
          <span className="font-semibold text-primary-text">{c.name}</span>
          <span className="text-meta text-muted font-numeric">{c.code}</span>
        </span>
      ),
      icon: (
        <span className="w-7 h-7 rounded-lg bg-primary-light text-accent-strong font-bold text-[0.6875rem] inline-flex items-center justify-center shrink-0">
          {(c.code || c.name || '?').slice(0, 2).toUpperCase()}
        </span>
      ),
      onClick: () => switchCompany(c.id),
    }));

  // The "+ Create new" quick-create menu moved into the AI Companion drawer
  // header (CopilotWidget) — it no longer lives next to the Dashboard tab.

  // Admin Panel was removed — Admin Center has its own main-nav tab now,
  // and a duplicate entry here was confusing. Divider moves onto Security
  // so there's still a visual gap between personal (Appearance / Menu) and
  // security (Security / Password / Sign out) groups.
  const who = user?.full_name || user?.email || '';
  const identityItems: DropdownItem[] = [
    ...(who ? [{ label: `Signed in as ${who}`, info: true }] : []),
    ...(companyItems.length > 0
      ? [{ label: `Company: ${company?.name || company?.code || ''}`, info: true }, ...(narrow ? companyItems : [])]
      : []),
    ...(branchMenu.visible
      ? [{ label: `Branch: ${branchMenu.label}`, info: true }, ...(narrow ? branchMenu.items : [])]
      : []),
  ];
  const platform = isPlatformAccount(user);
  const settingsItems: DropdownItem[] = platform ? [
    { label: 'Appearance', icon: <IconPalette size={14} />, onClick: () => setModal('appearance') },
    { label: 'Security', icon: <IconShield size={14} />, onClick: () => setModal('security'), divider: true },
    { label: 'Change Password', icon: <IconLock size={14} />, onClick: () => setModal('change-password') },
    { label: 'Sign out', icon: <IconLogOut size={14} />, onClick: async () => { await logout(); nav('/login'); }, tone: 'danger', divider: true },
  ] : [
    ...identityItems,
    { label: 'Help & Support', icon: <IconHelp size={14} />, onClick: () => openHelp(), divider: identityItems.length > 0 },
    { label: 'Appearance', icon: <IconPalette size={14} />, onClick: () => setModal('appearance') },
    { label: 'Menu Design', icon: <IconGrid size={14} />, onClick: () => setDesigning(true) },
    { label: 'Security', icon: <IconShield size={14} />, onClick: () => setModal('security'), divider: true },
    { label: 'Change Password', icon: <IconLock size={14} />, onClick: () => setModal('change-password') },
    { label: 'Sign out', icon: <IconLogOut size={14} />, onClick: async () => { await logout(); nav('/login'); }, tone: 'danger' as const, divider: true },
  ];

  return (
    <header
      // z-50 so the header's stacking context — and the menus that open
      // downward from it (Settings, company selector, quick-create) — sit
      // ABOVE BOTH the docked AI Companion panel (z-30) AND the SubNav
      // (z-40, raised so its own Print/action menus clear the dock). MainNav
      // must outrank SubNav or the SubNav bar (later in the DOM) paints over
      // these dropdowns where they overlap the action bar.
      // The bar is an inset, rounded "pill" floating on the page canvas
      // (matching the SubNav + bottom nav); the sticky wrapper keeps an
      // opaque bg-canvas so content scrolling underneath is masked in the
      // side/top gaps.
      className="sticky top-0 z-50 bg-canvas pt-cg pb-0"
    >
      {/* Menu-design banner sits ABOVE the nav bar so the tab row being
          reordered stays in its usual place while the instructions/actions
          float on top. */}
      {designing && (
        <div className="mx-2.5 mb-1 px-3 py-1 rounded-lg border border-primary/40 bg-primary/[0.06] flex items-center justify-between gap-3 text-[0.75rem]">
          <span className="text-primary-text font-medium inline-flex items-center gap-1.5 min-w-0">
            <IconGrid size={13} className="shrink-0" />
            <span className="truncate">Menu design — drag tabs to reorder. Changes save automatically.</span>
          </span>
          <span className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={() => setModal('menu-design')} className="px-2 py-0.5 rounded-md border border-border-light text-secondary hover:bg-slate-50">More options…</button>
            <button type="button" onClick={() => resetPrefs()} className="px-2 py-0.5 rounded-md border border-border-light text-secondary hover:bg-slate-50">Reset</button>
            <button type="button" onClick={() => setDesigning(false)} className="px-2.5 py-0.5 rounded-md bg-primary text-white font-medium hover:bg-primary/90">Done</button>
          </span>
        </div>
      )}

      {/* No brand mark here (owner, 2026-11-03): the Fritech logo sits at the
          left of the bottom bar (BottomBar). */}
      <div className="mx-2.5 pl-3 pr-1.5 h-[30px] [@media(pointer:coarse)]:h-12 rounded-xl border border-[#b8c4d6] bg-nav flex items-center gap-3">
        {platform ? (
          <nav data-mainnav aria-label="Main navigation" className="flex items-center flex-1 min-w-0">
            <span className="text-[0.75rem] font-semibold text-primary-text" aria-current="page">Manage Companies</span>
          </nav>
        ) : (
        <nav
          ref={navRef}
          data-mainnav
          aria-label="Main navigation"
          className="relative flex items-center gap-[4px] flex-1 min-w-0 justify-start"
          onKeyDown={(e) => {
            // ←/→ walk the main tabs (wrapping). Home/End jump to edges.
            // ↓ drops focus into the sub-nav for the current section — the
            // sub-nav handler can send focus back up with ↑. Enter still
            // follows the focused link natively.
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End', 'ArrowDown'].includes(e.key)) return;
            const links = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[data-navtab]'));
            if (links.length === 0) return;
            const idx = links.indexOf(document.activeElement as HTMLElement);
            if (idx < 0) return;
            e.preventDefault();
            if (e.key === 'ArrowDown') {
              // Land on the active sub-nav tab (the current page) so the
              // highlight is clearly visible where the user is, falling back
              // to the first tab/breadcrumb.
              const sub = document.querySelector<HTMLElement>('[data-subnav] [data-subtab][data-active]')
                || document.querySelector<HTMLElement>('[data-subnav] [data-subtab], [data-subnav] a[href], [data-subnav] button');
              sub?.focus();
              return;
            }
            let next = idx;
            // No wrap — Left at the first tab and Right at the last tab
            // are no-ops, so the arrow sequence never appears to cycle
            // past the bar's edges.
            if (e.key === 'ArrowRight') next = Math.min(idx + 1, links.length - 1);
            else if (e.key === 'ArrowLeft') next = Math.max(idx - 1, 0);
            else if (e.key === 'Home') next = 0;
            else if (e.key === 'End') next = links.length - 1;
            if (next === idx) return;
            const target = links[next];
            if (target) {
              target.focus();
              // Auto-activate via React Router's navigate(). Using
              // target.click() would also work but appeared to occasionally
              // shed focus after the re-render — nav() is an explicit,
              // focus-preserving route change.
              // Tabs are <button>s (no href) — the landing route rides on
              // data-href so arrow travel can still auto-activate the section.
              const href = target.getAttribute('data-href');
              if (href) nav(href, { replace: true });
            }
          }}
        >
          {designing ? (
            // Menu-design mode: every tab is a drag handle (no overflow).
            sections.map((s) => {
              return (
                <div
                  key={s.key}
                  data-design-key={s.key}
                  role="button"
                  aria-roledescription="draggable tab"
                  aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight"
                  tabIndex={0}
                  aria-label={`${s.label}: press Alt+Left or Alt+Right to move it`}
                  onKeyDown={(e) => {
                    if (!e.altKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
                    e.preventDefault();
                    const i = sections.findIndex((x) => x.key === s.key);
                    const j = e.key === 'ArrowLeft' ? i - 1 : i + 1;
                    if (j < 0 || j >= sections.length) return;
                    moveTab(s.key, sections[j].key);
                    const key = s.key;
                    requestAnimationFrame(() => navRef.current?.querySelector<HTMLElement>(`[data-design-key="${key}"]`)?.focus());
                  }}
                  draggable
                  onDragStart={() => setDragKey(s.key)}
                  onDragOver={(e) => { e.preventDefault(); if (overKey !== s.key) setOverKey(s.key); }}
                  onDragLeave={() => { if (overKey === s.key) setOverKey(null); }}
                  onDrop={(e) => { e.preventDefault(); if (dragKey) moveTab(dragKey, s.key); setDragKey(null); setOverKey(null); }}
                  onDragEnd={() => { setDragKey(null); setOverKey(null); }}
                  title="Drag, or Alt+Left / Alt+Right, to reorder"
                  className={`${compact ? 'w-[24px] h-[24px] justify-center' : 'px-2 h-[24px] gap-1'} rounded-full text-[0.75rem] font-medium inline-flex items-center border border-dashed cursor-grab active:cursor-grabbing select-none ${
                    dragKey === s.key ? 'opacity-40 border-primary' : overKey === s.key ? 'border-primary ring-2 ring-inset ring-primary bg-primary/10' : 'border-primary/50 bg-primary/[0.04] text-slate-900'
                  }`}
                >
                  <IconGrip size={11} className="text-muted shrink-0" />
                  <i className={`${s.fa} ${s.faColor} text-[0.875rem] w-4 text-center leading-none`} aria-hidden="true" />
                  {!compact && <span className="hidden lg:inline">{s.label}</span>}
                </div>
              );
            })
          ) : (
            <>
              {/* Hidden measurement row — all tabs at full size + a "More"
                  sentinel. It sits in its own clip box (absolute, inset-0,
                  overflow-hidden) so its full width never widens the page (it
                  made the ERP scroll sideways at 390px); the items' offsetWidth
                  is unaffected by the clip. The clip is on this box, not the
                  nav, so the More menu and focus rings are not cut.
                  `invisible` also takes it out of hit-testing and the a11y tree. */}
              <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
              <div ref={measureRef} className="absolute left-0 top-0 flex w-max items-center gap-[4px] whitespace-nowrap invisible">
                {sections.map((s) => {
                  return (
                    <div key={s.key} data-mi className={tabClass(active?.key === s.key)}>
                      <i className={`${s.fa} ${active?.key === s.key ? '' : s.faColor} text-[0.875rem] w-4 text-center leading-none`} aria-hidden="true" />
                      {!compact && <span className={active?.key === s.key ? 'inline' : 'hidden lg:inline'}>{s.label}</span>}
                    </div>
                  );
                })}
                <div data-mi-more className={moreClass(false)}>
                  {!compact && <span className="inline">More</span>}
                  <IconChevron size={10} />
                </div>
              </div>
              </div>

              {sections.filter((_, i) => isShown(i, visibleCount, activeIndex)).map((s) => {
                const isActive = active?.key === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => nav(sectionLanding(s))}
                    title={s.label}
                    aria-label={s.label}
                    data-navtab=""
                    data-href={sectionLanding(s)}
                    data-active-section={isActive || undefined}
                    aria-current={isActive ? 'page' : undefined}
                    className={tabClass(isActive)}
                  >
                    <i className={`${s.fa} ${isActive ? '' : s.faColor} text-[0.875rem] w-4 text-center leading-none`} aria-hidden="true" />
                    {!compact && <span className={isActive ? 'inline' : 'hidden lg:inline'}>{s.label}</span>}
                  </button>
                );
              })}

              {sections.some((_, i) => !isShown(i, visibleCount, activeIndex)) && (() => {
                const overflow = sections.filter((_, i) => !isShown(i, visibleCount, activeIndex));
                return (
                  <DropdownMenu
                    align="left"
                    label="More sections"
                    items={overflow.map((s) => ({
                      label: s.label,
                      icon: <i className={`${s.fa} ${s.faColor} text-[0.875rem] w-4 text-center leading-none`} aria-hidden="true" />,
                      onClick: () => nav(sectionLanding(s)),
                    }))}
                    trigger={
                      <div title="More sections" className={moreClass(false)}>
                        {!compact && <span className="hidden lg:inline">More</span>}
                        <IconChevron size={10} />
                      </div>
                    }
                  />
                );
              })()}
            </>
          )}
          <CustomNavTabs compact={compact} />
        </nav>
        )}

        <div className="flex items-center gap-1.5 shrink-0">
          {!platform && (
            <>
          <IconButton label="Search pages (Ctrl+Shift+P)" onClick={() => window.dispatchEvent(new Event('nexus:command-palette'))}>
            <IconSearch size={13} />
          </IconButton>
          <NotificationBell />
          <BranchPicker />
          <FavoritesMenu />
            </>
          )}
          {(() => {
            // Portal users (employee/manager role) can click their name to jump to
            // their My HR profile. Non-portal users (admins without an employee
            // record) keep a static status pill — clicking would only land on
            // "Profile unavailable".
            const isPortalUser = (user?.roles || []).some((r) => r === 'employee' || r === 'manager');
            const pillStyle = {
              padding: '2px 10px', borderRadius: '100px',
              border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)',
            } as const;
            const inner = (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                <span className="sr-only">Signed in as </span>
                <span className="text-primary-text font-medium">{user?.full_name || user?.email || '—'}</span>
              </>
            );
            return isPortalUser ? (
              <button
                type="button"
                onClick={() => nav('/portal/profile')}
                title="Go to My HR profile"
                data-user-pill=""
                className="hidden md:inline-flex items-center gap-1.5 bg-surface text-[0.6875rem] hover:bg-slate-50 transition cursor-pointer"
                style={pillStyle}
              >
                {inner}
              </button>
            ) : (
              <div
                className="hidden md:inline-flex items-center gap-1.5 bg-surface text-[0.6875rem] select-none pointer-events-none"
                data-user-pill=""
                style={pillStyle}
              >
                {inner}
              </div>
            );
          })()}
          {platform ? null : companyItems.length > 0 ? (
            <DropdownMenu
              align="right"
              label={`Switch company: ${company?.code || company?.name || ''}`}
              items={companyItems}
              className="hidden md:inline-block"
              trigger={
                <div
                  className="inline-flex items-center gap-1 bg-surface text-primary-text font-medium hover:bg-slate-50 transition cursor-pointer"
                  style={{
                    padding: '2px 14px',
                    borderRadius: '10px',
                    border: '1px solid var(--border)',
                    fontSize: '11px',
                    minWidth: '96px',
                    justifyContent: 'space-between',
                  }}
                  title="Switch company"
                >
                  <span>{company?.code || company?.name || '—'}</span>
                  <IconChevron size={10} />
                </div>
              }
            />
          ) : (
            <div
              className="hidden md:inline-flex items-center gap-1 bg-surface text-primary-text font-medium"
              style={{
                padding: '2px 14px',
                borderRadius: '10px',
                border: '1px solid var(--border)',
                fontSize: '11px',
                minWidth: '96px',
                justifyContent: 'center',
              }}
            >
              <span>{company?.code || company?.name || '—'}</span>
            </div>
          )}
          <DropdownMenu
            align="right"
            label="Settings"
            items={settingsItems}
            // The identity rows (and on a phone the company and branch rows)
            // make this menu taller than a short screen, a landscape phone
            // included: bound it to what is visible below the nav at every
            // width and let it scroll, so Sign out stays reachable.
            panelClassName={`max-h-[calc(100dvh-var(--app-nav-h,80px)-16px)] overflow-y-auto overscroll-contain${narrow ? ' max-w-[calc(100vw-20px)]' : ''}`}
            trigger={
              <div
                title="Settings"
                className="inline-flex items-center justify-center h-[24px] w-[24px] rounded-md text-secondary hover:text-primary-text hover:bg-slate-100 transition-colors"
              >
                <IconSettings size={14} />
              </div>
            }
          />
        </div>
      </div>

      <Modal
        open={modal !== null}
        onClose={() => setModal(null)}
        title={modal ? MODAL_TITLE[modal] : ''}
        width={modal ? MODAL_WIDTH[modal] : 'max-w-lg'}
      >
        {modal === 'appearance' && <AppearancePage embedded />}
        {modal === 'menu-design' && <MenuDesignPage embedded />}
        {modal === 'security' && <SecurityPage embedded />}
        {modal === 'change-password' && <ChangePasswordPage embedded onDone={() => setModal(null)} />}
      </Modal>
    </header>
  );
}
